"use client"

import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useState } from "react"
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary"
import { useAppContext } from "../../shared/functions/Context"
import { dataFormat } from "../../shared/functions/Directives"
import showModalFromId from "../../shared/functions/ModalShow"
import { ALL_TAB } from "../../shared/interfaces/IPerspectiveTabs"
import type Measure from "../../shared/models/Measure"
import type Objective from "../../shared/models/Objective"
import type { IScorecardMetadata } from "../../shared/models/ScorecardMetadata"
import MODAL_NAMES from "../dialogs/ModalName"
import { rateColor } from "../shared/functions/Scorecard"
import NoPerformanceData from "./NoPerformanceData"
import NoMeasures from "./no-measures/NoMeasures"
import type { IUser } from "../../shared/models/User"

interface IQ4ReviewMeasureTableItemProps {
  measure: Measure
  canUpdate: boolean
  q4Rated: boolean
}

const MeasureTableItem = observer((props: IQ4ReviewMeasureTableItemProps) => {
  const { api, store, ui } = useAppContext()
  const measure = props.measure.asJson

  const dataType = measure.dataType
  const dataSymbol = measure.dataSymbol

  const [ratingCss, setRatingCss] = useState("")

  const readMeasureComment = () => {
    store.measure.select(measure)
    // showModalFromId(MODAL_NAMES.EXECUTION.MEASURE_COMMENTS_Q4_MODAL)
  }

  const handleDispute = () => {
    store.measure.select(measure)
    showModalFromId(MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q4_MODAL)
  }

  const measureReadOnly = () => {
    store.measure.select(measure)
    showModalFromId(MODAL_NAMES.EXECUTION.MEASURE_READ_ONLY_MODAL)
  }

  useEffect(() => {
    if (measure.finalRating2) {
      const rateCss = rateColor(Number(measure.finalRating2), measure.isUpdated)
      setRatingCss(rateCss)
    } else if (measure.supervisorRating2) {
      const ratingCss = rateColor(Number(measure.supervisorRating2), measure.isUpdated)
      setRatingCss(ratingCss)
    } else {
      const ratingCss = rateColor(measure.autoRating2 ?? 0, measure.isUpdated)
      setRatingCss(ratingCss)
    }
  }, [measure, ratingCss])

  const disputeExists =
    measure.finalAssessmentDisputeReason ||
    measure.finalAssessmentPreferredRating ||
    measure.finalAssessmentDisputeType ||
    measure.finalAssessmentAdditionalComments

  const isResolved = Boolean(measure.finalAssessmentIsResolved)
  // max: mirrored HR's resolved indicator so employees instantly recognise closed disputes
  const disputeContainerClass = isResolved
    ? "bg-green-50 border-l-4 border-[#4bb543] rounded-lg p-4 mt-3"
    : "bg-[#fff3cd] border-l-4 border-[#faa05a] rounded-lg p-4 mt-3"
  const statusBadgeClass = isResolved
    ? "inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#dff5e5] text-[#2b6a2f] text-xs font-semibold"
    : "inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#ffe8cc] text-[#a35c22] text-xs font-semibold"

  return (
    <ErrorBoundary>
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200 mb-3">
        <div className="flex items-start gap-4">
          {/* Status Indicator */}
          <div
            className={`w-1 h-full rounded-full ${ratingCss === "rating-1" ? "bg-[#dc3545]" : ratingCss === "rating-2" ? "bg-[#faa05a]" : ratingCss === "rating-3" ? "bg-[#e7a637]" : ratingCss === "rating-4" ? "bg-[#84cbe9]" : "bg-[#4bb543]"}`}
          />

          <div className="flex-1">
            {/* Measure Description */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="text-base font-semibold text-[#3f6a8e] mb-1">{measure.description}</h4>
                <button
                  title="Comments"
                  className="text-[#84cbe9] hover:text-[#3f6a8e] transition-colors text-sm flex items-center gap-1"
                  onClick={readMeasureComment}
                >
                  <span data-uk-icon="icon: comments; ratio: 0.7" />
                  View Comments
                </button>
              </div>

              <button
                title={disputeExists ? "Edit Dispute" : "Dispute Rating"}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${disputeExists
                    ? "bg-[#faa05a] hover:bg-[#e7a637] text-white"
                    : "bg-[#dc3545] hover:bg-[#c82333] text-white"
                  }`}
                onClick={handleDispute}
              >
                {disputeExists ? "Edit Dispute" : "Dispute Rating"}
              </button>
            </div>

            {/* Ratings Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Rating 1</p>
                <p className="text-sm font-semibold text-gray-900">
                  {dataFormat(dataType, measure.rating1, dataSymbol)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Rating 2</p>
                <p className="text-sm font-semibold text-gray-900">
                  {dataFormat(dataType, measure.rating2, dataSymbol)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Rating 3</p>
                <p className="text-sm font-semibold text-gray-900">
                  {dataFormat(dataType, measure.rating3, dataSymbol)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Rating 4</p>
                <p className="text-sm font-semibold text-gray-900">
                  {dataFormat(dataType, measure.rating4, dataSymbol)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Rating 5</p>
                <p className="text-sm font-semibold text-gray-900">
                  {dataFormat(dataType, measure.rating5, dataSymbol)}
                </p>
              </div>
            </div>

            {/* Target and Progress */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-[#3f6a8e] mb-1">Target</p>
                <p className="text-sm font-semibold text-[#3f6a8e]">
                  {dataFormat(dataType, measure.annualTarget, dataSymbol)}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-[#3f6a8e] mb-1">Progress</p>
                <p className="text-sm font-semibold text-[#3f6a8e]">
                  {dataFormat(dataType, measure.quarter4Actual, dataSymbol)}
                </p>
              </div>
              <div
                className={`rounded-lg p-3 ${ratingCss === "rating-1" ? "bg-red-50" : ratingCss === "rating-2" ? "bg-orange-50" : ratingCss === "rating-3" ? "bg-yellow-50" : ratingCss === "rating-4" ? "bg-blue-50" : "bg-green-50"}`}
              >
                <p className="text-xs text-gray-600 mb-1">E-Rating</p>
                <p
                  className={`text-sm font-semibold ${ratingCss === "rating-1" ? "text-[#dc3545]" : ratingCss === "rating-2" ? "text-[#faa05a]" : ratingCss === "rating-3" ? "text-[#e7a637]" : ratingCss === "rating-4" ? "text-[#84cbe9]" : "text-[#4bb543]"}`}
                >
                  {measure.autoRating2?.toFixed(2) || "-"}
                </p>
              </div>
              <div
                className={`rounded-lg p-3 ${ratingCss === "rating-1" ? "bg-red-50" : ratingCss === "rating-2" ? "bg-orange-50" : ratingCss === "rating-3" ? "bg-yellow-50" : ratingCss === "rating-4" ? "bg-blue-50" : "bg-green-50"}`}
              >
                <p className="text-xs text-gray-600 mb-1">S-Rating</p>
                <p
                  className={`text-sm font-semibold ${ratingCss === "rating-1" ? "text-[#dc3545]" : ratingCss === "rating-2" ? "text-[#faa05a]" : ratingCss === "rating-3" ? "text-[#e7a637]" : ratingCss === "rating-4" ? "text-[#84cbe9]" : "text-[#4bb543]"}`}
                >
                  {measure.supervisorRating2?.toFixed(2) || "-"}
                </p>
              </div>
              <div
                className={`rounded-lg p-3 ${ratingCss === "rating-1" ? "bg-red-50" : ratingCss === "rating-2" ? "bg-orange-50" : ratingCss === "rating-3" ? "bg-yellow-50" : ratingCss === "rating-4" ? "bg-blue-50" : "bg-green-50"}`}
              >
                <p className="text-xs text-gray-600 mb-1">F-Rating</p>
                <p
                  className={`text-sm font-semibold ${ratingCss === "rating-1" ? "text-[#dc3545]" : ratingCss === "rating-2" ? "text-[#faa05a]" : ratingCss === "rating-3" ? "text-[#e7a637]" : ratingCss === "rating-4" ? "text-[#84cbe9]" : "text-[#4bb543]"}`}
                >
                  {measure.finalRating2?.toFixed(2) || "-"}
                </p>
              </div>
            </div>

            {/* Dispute Details */}
            {disputeExists && (
              <div className={disputeContainerClass}>
                <div className="flex items-start gap-2">
                  <span
                    data-uk-icon="icon: warning; ratio: 0.8"
                    className={`mt-0.5 ${isResolved ? "text-[#4bb543]" : "text-[#e7a637]"}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-[#856404]">Dispute Details</p>
                      <span className={statusBadgeClass}>
                        {isResolved ? "Resolved" : "Awaiting Resolution"}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium text-gray-700">Reason:</span>{" "}
                        <span className="text-gray-900">{measure.finalAssessmentDisputeReason}</span>
                      </p>
                      {measure.finalAssessmentPreferredRating && (
                        <p>
                          <span className="font-medium text-gray-700">Preferred Rating:</span>{" "}
                          <span className="text-gray-900">{measure.finalAssessmentPreferredRating}</span>
                        </p>
                      )}
                      {measure.finalAssessmentDisputeType && (
                        <p>
                          <span className="font-medium text-gray-700">Type:</span>{" "}
                          <span className="text-gray-900">{measure.finalAssessmentDisputeType}</span>
                        </p>
                      )}
                      {measure.finalAssessmentAdditionalComments && (
                        <p>
                          <span className="font-medium text-gray-700">Additional Comments:</span>{" "}
                          <span className="text-gray-900">{measure.finalAssessmentAdditionalComments}</span>
                        </p>
                      )}
                      {isResolved && measure.finalAssessmentSupervisorDisputeResolution && (
                        <p className="text-gray-900">
                          <span className="font-medium text-gray-700">Supervisor Resolution:</span>{" "}
                          {measure.finalAssessmentSupervisorDisputeResolution}
                        </p>
                      )}
                      {isResolved && measure.finalAssessmentEmployeeDisputeResolution && (
                        <p className="text-gray-900">
                          <span className="font-medium text-gray-700">Employee Resolution:</span>{" "}
                          {measure.finalAssessmentEmployeeDisputeResolution}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
})

interface IQ4ReviewProps {
  measures: Measure[]
  canUpdate: boolean
  q4Rated: boolean
}
const MeasureTable = observer((props: IQ4ReviewProps) => {
  const { measures, canUpdate, q4Rated } = props

  return (
    <ErrorBoundary>
      <div className="space-y-3">
        {measures.length !== 0 && (
          <div className="space-y-3">
            {measures.map((measure) => (
              <ErrorBoundary key={measure.asJson.id}>
                <MeasureTableItem measure={measure} canUpdate={canUpdate} q4Rated={q4Rated} />
              </ErrorBoundary>
            ))}
          </div>
        )}
        {measures.length === 0 && <NoMeasures />}
      </div>
    </ErrorBoundary>
  )
})

interface IQ4Props {
  agreement: IScorecardMetadata
  selectedUser: IUser
  currentUser: IUser
  measures: Measure[]
  objectives: Objective[]
}

const EmployeeQ4ReviewCycle = observer((props: IQ4Props) => {
  const { store, ui, api } = useAppContext()
  const [tab, setTab] = useState(ALL_TAB.id)

  const { agreement, selectedUser, currentUser, measures, objectives } = props
  const q4Rated = agreement.assessmentRated

  const isDisabled = useMemo(
    () => !(agreement.quarter4Review.status === "submitted"),
    [agreement.quarter4Review.status],
  )

  const canUpdate = useMemo(() => agreement.quarter4Review.status === "submitted", [agreement])

  const incompleteReviewError = measures.some((m) => m.asJson.supervisorRating2 === null)

  if (agreement.quarter4Review.status === "pending" || agreement.quarter4Review.status === "in-progress")
    return (
      <ErrorBoundary>
        <NoPerformanceData title="Final assessment progress not submitted." />
      </ErrorBoundary>
    )

  return (
    <ErrorBoundary>
      <MeasureTable measures={measures} canUpdate={canUpdate} q4Rated={q4Rated} />
    </ErrorBoundary>
  )
})

export default EmployeeQ4ReviewCycle
